import type { ReactNode } from "react"

import { peakWindowFromRateCurve } from "@logic/lib/battery-arbitrage"
import { isPowurCareWarrantyName } from "@logic/lib/powur-os"

import type { BatteryArbitragePresentationDto } from "~/api/dto/battery-arbitrage.dto"
import type { BatteryComputePaymentShareDto } from "~/api/dto/battery-proposal.dto"

import type { BatteryPresentationProposal } from "./presentation-proposal"
import { GradText } from "./primitives"
import { NextSteps } from "../ProposalPresentation/NextSteps"
import { BatteryAiComputeSection } from "./sections/ai-compute"
import { Arbitrage } from "./sections/arbitrage"
import { BatteryFooter, BatteryHeader, SectionRail } from "./sections/chrome"
import { Hero } from "./sections/hero"
import {
  Crisis,
  HiddenCost,
  PeakTrap,
  SmartResponse,
} from "./sections/marketing"
import { Specs } from "./sections/specs"
import { Outage, PowurCare } from "./sections/story"
import { NEXT_STEPS } from "./static-copy"
import { HomeownerApp } from "../ProposalPresentation/HomeownerApp"
import type { UpdatesRailState } from "../ProposalPresentation/updates/use-updates-panel-state"

/**
 * The Battery presentation itself, in section order. It reads presentation
 * data only, so the seller page and the public link render the same document.
 * Everything a seller may act on arrives through a slot: `chrome` for the
 * floating Setup entry and `investment` for the money section, which the
 * public route replaces with its own read-only card.
 *
 * `arbitrage` carries the saved result and any server-computed what-if points.
 * The dial selects a point locally; the hero, projections and export continue
 * to use the saved result.
 *
 * A saved AI compute node renders before the investment, apart from the
 * battery's savings: its income and electricity are never part of them.
 * `computeAction` is the seller's link to the AI Compute setup step, and
 * `computePaymentShare` the gateway's split of its payout at the monthly
 * payment, when there is one to set it beside.
 *
 * `rail` carries the seller's Updates panel state: the rail steps aside while
 * the panel is open and badges out-of-date sections while it's minimized.
 */
export function BatteryPresentation({
  proposal,
  arbitrage,
  investment,
  chrome = null,
  computeAction = null,
  computePaymentShare = null,
  interactiveSavings = true,
  rail,
}: {
  proposal: BatteryPresentationProposal
  arbitrage: BatteryArbitragePresentationDto | null
  investment: ReactNode
  chrome?: ReactNode
  computeAction?: ReactNode
  computePaymentShare?: BatteryComputePaymentShareDto | null
  interactiveSavings?: boolean
  rail?: UpdatesRailState
}): JSX.Element {
  const peakWindow = arbitrage
    ? peakWindowFromRateCurve(arbitrage.representativeRateCurve)
    : null
  // Optional coverage renders only when the saved Extended Warranty snapshot
  // classifies as PowurCare by name — never for other families or no selection.
  const showPowurCare = isPowurCareWarrantyName(proposal.extendedWarranty?.name)

  return (
    <>
      <BatteryHeader proposal={proposal} />
      <SectionRail
        showSavings={arbitrage?.peakUsageScenario != null}
        showPowurCare={showPowurCare}
        showAiCompute={proposal.aiCompute != null}
        hidden={rail?.hidden}
        staleSections={rail?.staleSections}
      />
      {chrome}
      <Hero proposal={proposal} arbitrage={arbitrage} />
      <Crisis />
      <PeakTrap peakWindow={peakWindow} />
      <HiddenCost />
      <SmartResponse />
      <Specs proposal={proposal} />
      <Arbitrage arbitrage={arbitrage} interactive={interactiveSavings} />
      <Outage />
      {showPowurCare ? <PowurCare /> : null}
      {proposal.aiCompute ? (
        <BatteryAiComputeSection
          aiCompute={proposal.aiCompute}
          paymentShare={computePaymentShare}
          action={computeAction}
        />
      ) : null}
      {investment}
      <NextSteps
        heading={
          <>
            From kick-off to backup power in about <GradText>5 weeks</GradText>.
          </>
        }
        steps={NEXT_STEPS}
      />
      <HomeownerApp
        heading={
          <>
            Your entire energy storage experience,{` `}
            <GradText>simplified</GradText>.
          </>
        }
      />
      <BatteryFooter proposal={proposal} />
    </>
  )
}
